import type { Formato, Ornamento as TipoOrnamento, Paleta } from '@miolos/core';
import { ehOrnamentoDePapel, medirPagina } from '@miolos/core';
import type { ConfigPapel } from '@miolos/core';

interface Props {
  tipo: TipoOrnamento;
  paleta: Paleta;
  formato: Formato;
  /** Lado da encadernação, para não colocar ornamento no vinco. */
  lado: 'ESQUERDO' | 'DIREITO';
  /** Orientação e margem escolhidas no assistente. */
  papel?: ConfigPapel;
}

/**
 * Ornamentos decorativos das páginas.
 * Todos em SVG, com cores da paleta e opacidade baixa — decoram sem competir
 * com a escrita (e sem gastar tinta além do necessário).
 */
export function Ornamento({ tipo, paleta, formato, lado, papel }: Props) {
  const medidas = medirPagina(formato, { orientacao: papel?.orientacao, lado });
  const { larguraMm, alturaMm } = medidas;

  if (tipo === 'NENHUM') return null;

  /*
   * Os padrões de folha inteira (pontilhado, quadriculado, ondinhas e confete)
   * são pintados pelo `PapelDecorado`, que entra **por baixo** das imagens do
   * cliente. Aqui em cima, por cima de tudo, eles apareceriam sobre a arte de
   * fundo de quem enviou uma.
   */
  if (ehOrnamentoDePapel(tipo)) return null;

  const base: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 900,
    pointerEvents: 'none',
    overflow: 'hidden',
  };

  if (tipo === 'LINHA_FINA') {
    return (
      <div style={base}>
        <div
          style={{
            position: 'absolute',
            left: `${medidas.conteudoXMm}mm`,
            width: `${medidas.larguraConteudoMm}mm`,
            top: `${Math.max(medidas.conteudoYMm - 4, 5)}mm`,
            height: '0.35mm',
            background: paleta.destaque,
            opacity: 0.75,
          }}
        />
      </div>
    );
  }

  if (tipo === 'ARCO') {
    return (
      <div style={base}>
        <svg
          viewBox={`0 0 ${larguraMm} ${alturaMm}`}
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0 }}
          aria-hidden
        >
          <circle
            cx={larguraMm + 8}
            cy={-6}
            r={larguraMm * 0.62}
            fill="none"
            stroke={paleta.principal}
            strokeWidth={0.5}
            opacity={0.16}
          />
          <circle
            cx={larguraMm + 8}
            cy={-6}
            r={larguraMm * 0.44}
            fill="none"
            stroke={paleta.destaque}
            strokeWidth={0.4}
            opacity={0.14}
          />
          <circle
            cx={-6}
            cy={alturaMm + 6}
            r={larguraMm * 0.3}
            fill={paleta.principalClaro}
            opacity={0.12}
          />
        </svg>
      </div>
    );
  }

  if (tipo === 'CANTO') {
    const tamanho = Math.max(larguraMm * 0.09, 9);
    const espessura = 0.4;
    const opacidade = 0.4;
    const deslocamento = 4;
    const cor = paleta.destaque;
    return (
      <div style={base}>
        {(
          [
            { top: deslocamento, left: deslocamento, rot: 0 },
            { top: deslocamento, right: deslocamento, rot: 90 },
            { bottom: deslocamento, right: deslocamento, rot: 180 },
            { bottom: deslocamento, left: deslocamento, rot: 270 },
          ] as const
        ).map((canto, i) => (
          <svg
            key={i}
            width={`${tamanho}mm`}
            height={`${tamanho}mm`}
            viewBox="0 0 100 100"
            style={{
              position: 'absolute',
              top: 'top' in canto ? `${canto.top}mm` : undefined,
              bottom: 'bottom' in canto ? `${canto.bottom}mm` : undefined,
              left: 'left' in canto ? `${canto.left}mm` : undefined,
              right: 'right' in canto ? `${canto.right}mm` : undefined,
              transform: `rotate(${canto.rot}deg)`,
              opacity: opacidade,
            }}
            aria-hidden
          >
            <path
              d="M2 40 L2 2 L40 2"
              fill="none"
              stroke={cor}
              strokeWidth={espessura * 4}
              strokeLinecap="square"
            />
          </svg>
        ))}
      </div>
    );
  }

  if (tipo === 'FLORAL') {
    return (
      <div style={base}>
        <svg
          viewBox={`0 0 ${larguraMm} ${alturaMm}`}
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0 }}
          aria-hidden
        >
          <g
            opacity={0.22}
            stroke={paleta.principal}
            strokeWidth={0.35}
            fill="none"
            strokeLinecap="round"
            transform={`translate(${larguraMm - 34}, ${alturaMm - 46})`}
          >
            <path d="M0 40 C 6 24, 14 12, 28 2" />
            <path d="M8 30 C 2 26, 0 20, 2 14 C 8 16, 11 21, 8 30 Z" fill={paleta.destaque} opacity={0.5} />
            <path d="M16 20 C 12 14, 13 8, 17 3 C 22 7, 23 14, 16 20 Z" fill={paleta.destaque} opacity={0.5} />
            <path d="M12 36 C 20 34, 26 30, 30 24" />
          </g>
          <g
            opacity={0.2}
            stroke={paleta.principal}
            strokeWidth={0.35}
            fill="none"
            strokeLinecap="round"
            transform={`translate(6, 8) rotate(180) scale(0.8)`}
          >
            <path d="M0 40 C 6 24, 14 12, 28 2" />
            <path d="M8 30 C 2 26, 0 20, 2 14 C 8 16, 11 21, 8 30 Z" fill={paleta.destaque} opacity={0.5} />
            <path d="M12 36 C 20 34, 26 30, 30 24" />
          </g>
        </svg>
      </div>
    );
  }

  // GEOMETRICO
  return (
    <div style={base}>
      <svg
        viewBox={`0 0 ${larguraMm} ${alturaMm}`}
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        <g opacity={0.13}>
          <polygon
            points={`0,${alturaMm * 0.22} ${larguraMm * 0.3},0 0,${alturaMm * 0.34}`}
            fill={paleta.principal}
          />
          <circle cx={larguraMm * 0.82} cy={alturaMm * 0.09} r={larguraMm * 0.1} fill={paleta.destaque} />
          <rect
            x={larguraMm * 0.68}
            y={alturaMm * 0.88}
            width={larguraMm * 0.4}
            height={alturaMm * 0.18}
            fill={paleta.principalClaro}
          />
        </g>
      </svg>
    </div>
  );
}
